import React from 'react';
import SignalBadge from './SignalBadge';

const VOTE_LABELS = {
  sma_cross: 'SMA 50/200 Cross',
  price_vs_sma200: 'Price vs SMA 200',
  ema_cross: 'EMA 12/26 Cross',
  macd: 'MACD Histogram',
  rsi: 'RSI (14)',
  bollinger: 'Bollinger Bands',
  stochastic: 'Stochastic',
  williams_r: 'Williams %R',
  adx: 'ADX (14)',
  obv: 'OBV vs Price',
  volume: 'Volume Spike',
  fibonacci: 'Fibonacci Levels',
  pivot: 'Daily Pivot',
  sr: 'Support/Resistance',
};

const reasonText = (key, vote, ind) => {
  const sign = vote > 0 ? '↑' : vote < 0 ? '↓' : '·';
  const fmt = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));
  switch (key) {
    case 'sma_cross':       return `${sign} SMA50 ${fmt(ind.sma_50)} / SMA200 ${fmt(ind.sma_200)}`;
    case 'price_vs_sma200': return `${sign} Close ${fmt(ind.close)} vs SMA200 ${fmt(ind.sma_200)}`;
    case 'ema_cross':       return `${sign} EMA12 ${fmt(ind.ema_12)} / EMA26 ${fmt(ind.ema_26)}`;
    case 'macd':            return `${sign} hist ${fmt(ind.macd_hist, 3)}`;
    case 'rsi':             return `${sign} RSI ${fmt(ind.rsi_14, 1)}`;
    case 'bollinger':       return `${sign} BB% ${fmt(ind.bb_pct, 2)}`;
    case 'stochastic':      return `${sign} %K ${fmt(ind.stoch_k, 1)} / %D ${fmt(ind.stoch_d, 1)}`;
    case 'williams_r':      return `${sign} %R ${fmt(ind.williams_r, 1)}`;
    case 'adx':             return `${sign} ADX ${fmt(ind.adx, 1)} (+DI ${fmt(ind.plus_di, 1)} / -DI ${fmt(ind.minus_di, 1)})`;
    case 'obv':             return `${sign} OBV slope ${fmt(ind.obv_slope, 4)}`;
    case 'volume':          return `${sign} vol×${fmt(ind.volume_ratio, 2)} avg`;
    case 'fibonacci':       return `${sign} near retracement level`;
    case 'pivot':            return `${sign} close ${fmt(ind.close)} vs P ${fmt(ind.pivots?.P)}`;
    case 'sr':              return `${sign} near S/R level`;
    default:                return `${sign} ${vote}`;
  }
};

const ConsolidatedSummaryCard = ({ summary }) => {
  if (!summary) return null;
  const score = Number(summary.score || 0);
  const indicators = summary.indicators || {};
  const votes = summary.votes || {};

  // Score gauge: -1 (red) → 0 (gray) → +1 (green) — represented as a horizontal bar
  const pctFill = ((score + 1) / 2) * 100;
  const fillColor = score > 0.15
    ? 'bg-green-500' : score < -0.15
    ? 'bg-red-500' : 'bg-gray-400';

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Left: Composite signal + score */}
      <div className="md:col-span-1 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-4 flex flex-col justify-center items-center">
        <div className="text-[10px] uppercase tracking-[2px] text-gray-400 mb-2">Composite Signal</div>
        <SignalBadge signal={summary.signal} size="lg" />
        <div className="mt-4 w-full">
          <div className="flex justify-between text-[10px] text-gray-400 mb-1">
            <span>SELL</span>
            <span>HOLD</span>
            <span>BUY</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-800 h-2 rounded relative">
            <div
              className={`absolute top-0 h-2 ${fillColor} rounded`}
              style={{ left: `${Math.min(pctFill, 50)}%`, width: `${Math.abs(pctFill - 50)}%` }}
            />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-px h-3 bg-gray-500" />
          </div>
          <div className="text-center text-lg font-semibold mt-2 font-serif text-gray-900 dark:text-gray-100">
            {score >= 0 ? '+' : ''}{score.toFixed(2)}
          </div>
          <div className="text-center text-[10px] text-gray-400">
            {votes && (
              <>
                {Object.values(votes).filter(v => v > 0).length} bullish ·{' '}
                {Object.values(votes).filter(v => v < 0).length} bearish ·{' '}
                {Object.values(votes).filter(v => v === 0).length} neutral
              </>
            )}
          </div>
        </div>
      </div>

      {/* Right: Vote table */}
      <div className="md:col-span-2 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-4">
        <div className="text-[10px] uppercase tracking-[2px] text-gray-400 mb-3">Indicator Votes</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs">
          {Object.entries(votes).map(([key, vote]) => {
            const color = vote > 0
              ? 'text-green-600 dark:text-green-400'
              : vote < 0
              ? 'text-red-500 dark:text-red-400'
              : 'text-gray-500 dark:text-gray-400';
            const dot = vote > 0 ? 'bg-green-500' : vote < 0 ? 'bg-red-500' : 'bg-gray-400';
            return (
              <div key={key} className="flex items-center gap-2 border-b border-gray-100 dark:border-gray-900 pb-1">
                <span className={`inline-block w-2 h-2 rounded-full ${dot} shrink-0`} />
                <span className="font-medium text-gray-700 dark:text-gray-300 shrink-0 w-32">
                  {VOTE_LABELS[key] || key}
                </span>
                <span className={`flex-1 truncate ${color}`} title={reasonText(key, vote, indicators)}>
                  {reasonText(key, vote, indicators)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ConsolidatedSummaryCard;
